import {
  Button,
  ButtonGroup,
  CheckableAvatarSelectItem,
  DropdownSelect,
  FlexBox,
  Text,
} from '@dixa/lib-shared-habla';
import type { ReadSearchApiArg } from 'lib-search-types';
import { UserType } from 'lib-shared-types-api';
import { useCallback, useMemo, useState } from 'react';
import { useUpdateEffect } from 'react-use';

import { useLazyReadSearchInfiniteQuery } from 'apis/search';

type AuthorItem = {
  readonly id: string;
  readonly name: string;
  readonly avatar_url?: string | null;
};

const userTypeToLabel: Record<UserType, string> = {
  member: 'Agent',
  contact: 'Contact',
  bot: 'Bot',
};

/**
 * The search arg for listing the users that can author audit-log events.
 */
const getInitialArg = (
  userTypeFilter: UserType | undefined,
): ReadSearchApiArg<'users'> => ({
  scope: 'users',
  sortBy: { field: 'display_name', order: 'asc' },
  ...(userTypeFilter
    ? {
        filters: {
          strategy: 'all',
          filters: [
            {
              field: 'user_type',
              operator: 'is_one',
              values: [
                {
                  value: userTypeFilter,
                },
              ],
            },
          ],
        },
      }
    : {}),
});

type ActivityLogAuthorFilterProps = {
  /** The currently selected author ids. */
  readonly value: readonly string[];
  /** Called when the selected author ids change. */
  readonly onChange: (authorIds: string[]) => void;
};

/** Represents the component for filtering the activity log by author. */
export const ActivityLogAuthorFilter = ({
  value,
  onChange,
}: ActivityLogAuthorFilterProps): JSX.Element => {
  const [userTypeFilter, setUserTypeFilter] = useState<UserType>();
  const [readSearchUsers, { results: users, hasNextPage, fetchNextPage }] =
    useLazyReadSearchInfiniteQuery<'users'>();

  const [searchTerm, setSearchTerm] = useState<string>();

  useUpdateEffect(() => {
    readSearchUsers(
      {
        ...getInitialArg(userTypeFilter),
        query: searchTerm,
      },
      { debounceTime: 300 },
    );
  }, [readSearchUsers, userTypeFilter, searchTerm]);

  const items = useMemo<readonly AuthorItem[]>(
    () =>
      users.map(({ id, name, email, phone_number, avatar_url }) => ({
        id,
        name: name || email || phone_number || id,
        avatar_url,
      })),
    [users],
  );

  const handleChange = useCallback(
    (item: AuthorItem, checked: boolean) => {
      onChange(
        checked ? [...value, item.id] : value.filter((id) => id !== item.id),
      );
    },
    [onChange, value],
  );

  const handleClear = useCallback(() => onChange([]), [onChange]);

  const handleSearch = useCallback((search?: string) => {
    setSearchTerm(search?.trim() || undefined);
  }, []);

  const handleToggle = useCallback(
    (isOpen: boolean) => {
      if (isOpen && !users.length) {
        readSearchUsers({
          ...getInitialArg(userTypeFilter),
          query: searchTerm,
        });
      } else if (!isOpen) {
        setSearchTerm(undefined);
      }
    },
    [readSearchUsers, userTypeFilter, searchTerm, users.length],
  );

  return (
    <DropdownSelect
      keepOpen
      hasSearch
      noArrow={false}
      items={items as AuthorItem[]}
      selectedItems={value as string[]}
      itemComponent={CheckableAvatarSelectItem}
      buttonProps={{ variant: 'tertiary', label: value.length || undefined }}
      dropdownProps={{ style: { minWidth: 280 } }}
      pagination={{
        previousEnable: false,
        nextEnable: hasNextPage,
        infinite: true,
        onNext: () => Promise.resolve(fetchNextPage()),
      }}
      onChange={handleChange}
      onClear={handleClear}
      onSearch={handleSearch}
      onToggle={handleToggle}
      optionComponent={
        <FlexBox
          flexDirection="column"
          gap="200"
          padding="0 200 200"
          alignItems="center"
        >
          <Text.Paragraph width="100%" variant="3">
            Filter by user type
          </Text.Paragraph>
          <ButtonGroup variant="secondary">
            {Object.keys(userTypeToLabel).map((filter) => (
              <Button
                key={filter}
                active={userTypeFilter === filter}
                onClick={() =>
                  setUserTypeFilter(
                    userTypeFilter === filter
                      ? undefined
                      : (filter as UserType),
                  )
                }
              >
                {userTypeToLabel[filter]}
              </Button>
            ))}
          </ButtonGroup>
        </FlexBox>
      }
    >
      Authors
    </DropdownSelect>
  );
};
